<template>
  <div>
    <div v-if="showCalendar" class="calendar-dots">
      <v-row>
        <v-col>
          <span class="caption grey-navy--text font-weight-regular text-center">
            {{ getSelectedDate }}
          </span>
        </v-col>
      </v-row>
      <v-row align="center" justify="center">
        <v-col class="pb-0 relative mt-n2">
          <v-icon color="secondary" @click="updateWeek(-1)" class="navigate-week left">keyboard_arrow_left</v-icon>
          <v-icon @click="updateWeek(1)" color="secondary" class="navigate-week right">keyboard_arrow_right</v-icon>
          <v-tabs v-model="dayTab" :centered="true" :icons-and-text="true" color="secondary" slider-size="0">
            <v-tab
              v-for="(key, index) in days"
              :key="index"
              :icons-and-text="true"
              class="py-2 mb-0 pl-1 pr-1 pr-sm-2 day-tab"
              align="start"
              @click.native="changeDate(index)"
            >
              <v-avatar
                class="elevation-2 caption navy--text"
                :class="[
                  dayTab == index ? accentColor : '',
                  { 'today-border': formatDate(key.date) == formatDate(new Date()) },
                ]"
                :style="borderColorStyle"
                size="30"
                outlined
              >
                <span :class="{ 'white--text': dayTab == index }">
                  {{ key.date.getDate() }}
                </span>
              </v-avatar>
            </v-tab>
          </v-tabs>
        </v-col>
        <v-skeleton-loader v-if="$apollo.loading" type="avatar, avatar, avatar, avatar" class="d-flex" />
        <v-col v-else-if="eligible" cols="12" class="d-flex mt-n7 streak-calendar-dots justify-center">
          <div v-for="(breakdown, index) in eligible.dayBreakdown" :key="index" class="w-40">
            <!-- Workout dots -->
            <!-- filled in -->
            <div v-if="breakdown.workoutImage" class="relative">
              <v-avatar :size="21" class="sweaty-selfie-cal">
                <img alt="Sweaty selfie" :src="breakdown.workoutImage" />
              </v-avatar>
              <v-icon color="pink" class="tracking-icon" :size="30">mdi-circle</v-icon>
            </div>
            <div v-else-if="!breakdown.workoutImage && breakdown.loggedWorkout" class="d-flex justify-center">
              <v-icon color="pink" class="half-tracking-icon" :size="30">mdi-circle</v-icon>
            </div>
            <div v-else-if="breakdown.isRestDay">
              <v-icon color="grey darken-2" class="tracking-icon" :size="30">mdi-circle</v-icon>
            </div>
            <!-- not filled in  -->
            <v-icon v-else color="pink" class="tracking-icon" :size="30">
              mdi-circle-outline
            </v-icon>

            <!-- Nutrition dots -->
            <!-- filled in -->
            <div v-if="breakdown.greenDay" class="nutrition-icon">
              <span v-if="breakdown.loggedNutrition" class="white--text nutrition-no">
                {{ breakdown.loggedNutrition }}
              </span>
              <v-icon color="green" class="tracking-icon" :size="30">mdi-circle</v-icon>
            </div>
            <!-- not filled in  -->
            <div v-else class="nutrition-icon relative">
              <span v-if="breakdown.loggedNutrition" class="green--text nutrition-no-cal">
                {{ breakdown.loggedNutrition }}
              </span>
              <v-icon color="green" class="tracking-icon" :size="30">
                mdi-circle-outline
              </v-icon>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <div v-else>
      <div v-if="this.week && pastWeek" class="font-weight-bold text-center">{{ weeksAgo }} week<span v-if="weeksAgo > 1">s</span> ago</div>
      <v-skeleton-loader v-if="$apollo.loading" type="avatar, avatar, avatar, avatar" class="d-flex" />
      <div
        v-else-if="eligible"
        class="mw-40 mb-0 day-tracking d-flex calendar-dots justify-center streak-calendar-dots"
      >
        <div v-for="(breakdown, index) in eligible.dayBreakdown" :key="index">
          <!-- Workout dots -->
          <!-- filled in -->
          <div v-if="breakdown.workoutImage" class="relative">
            <v-avatar :size="21" class="sweaty-selfie">
              <img alt="Sweaty selfie" :src="breakdown.workoutImage" />
            </v-avatar>
            <v-icon color="pink" class="tracking-icon" :size="30">mdi-circle</v-icon>
          </div>
          <div v-else-if="!breakdown.workoutImage && breakdown.loggedWorkout" class="d-flex">
            <v-icon color="pink" class="half-tracking-icon" :size="30">mdi-circle</v-icon>
          </div>
          <div v-else-if="breakdown.isRestDay" class="relative">
            <v-icon color="grey darken-2" class="tracking-icon" :size="30">mdi-circle</v-icon>
          </div>
          <!-- not filled in  -->
          <v-icon v-else color="pink" class="tracking-icon" :size="30">
            mdi-circle-outline
          </v-icon>

          <!-- Nutrition dots -->
          <!-- filled in -->
          <div v-if="breakdown.greenDay" class="nutrition-icon relative">
            <span v-if="breakdown.loggedNutrition" class="white--text nutrition-no">
              {{ breakdown.loggedNutrition }}
            </span>
            <v-icon color="green" class="tracking-icon" :size="30">mdi-circle</v-icon>
          </div>
          <!-- not filled in  -->
          <div v-else class="nutrition-icon relative">
            <span v-if="breakdown.loggedNutrition" class="green--text nutrition-no">
              {{ breakdown.loggedNutrition }}
            </span>
            <v-icon color="green" class="tracking-icon" :size="30">
              mdi-circle-outline
            </v-icon>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showArrows" class="d-flex justify-space-between mx-n5 mt-n11 mb-5">
      <v-btn icon small narrow color="navy" @click="changeWeek(-1)">
        <v-icon large>mdi-chevron-left</v-icon>
      </v-btn>
      <v-spacer />
      <v-btn v-if="pastWeek" icon small narrow color="navy" @click="changeWeek(1)">
        <v-icon large>mdi-chevron-right</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { PRIZE_ELIGIBILITY_QUERY } from "@/graphql/queries/prizeCalendar";
import { getDayOfWeekString } from "@/lib/timezone";
import CalendarDotMixin from "@/components/shared/mixins/CalendarDotMixin";

export default {
  name: "ClientWeekDots",
  mixins: [CalendarDotMixin],
  props: {
    userId: {
      type: String,
    },
    /**
     * Use this property to inject the data directly into the component
     */
    showCalendar: {
      type: Boolean,
      default: false,
    },
    showArrows: {
      type: Boolean,
      default: false,
    },
    accentColor: {
      type: String,
      required: false,
      default: "exercise",
    },
  },
  data() {
    return {
      setSelectedDate: null,
    };
  },
  computed: {
    pastWeek() {
      let thisWeek = getDayOfWeekString(1, false, this.$moment().format("YYYY-MM-DD"), false).substring(0, 10);

      return this.$moment(this.week).isBefore(thisWeek);
    },
    weeksAgo() {
      return this.$moment().diff(this.$moment(this.week), "weeks");
    },
    startOfWeek() {
      if (!this.week) {
        return this.$moment()
          .startOf("week")
          .add(1, "days")
          .format("YYYY-MM-DD");
      } else {
        return this.$moment(this.week).format("YYYY-MM-DD");
      }
    },
  },
  methods: {
    changeDate(index) {
      this.dayTab = index;
      let newDate = this.$moment(this.week, "YYYY-MM-DD")
        .startOf("week")
        .add(index + 1, "days");
      this.setSelectedDate = newDate;
    },
    setDay() {
      var today = new Date();
      var day = today.getDay();
      this.setSelectedDate = this.$moment();

      this.dayTab = day - (day == 0 ? -6 : 1);
    },
    past(index) {
      return this.$moment(this.eligible?.dayBreakdown[index].date).isBefore(this.$moment());
    },
    changeWeek(direction) {
      let newDate = this.$moment(this.week, "YYYY-MM-DD").add(7 * direction, "days");
      this.week = getDayOfWeekString(1, false, newDate.format("YYYY-MM-DD"), false).substring(0, 10);
      this.setSelectedDate = this.week;
      this.$apollo.queries.eligible.refetch();
    },
  },
  apollo: {
    eligible: {
      query: PRIZE_ELIGIBILITY_QUERY,
      variables() {
        return {
          date: this.startOfWeek,
          userId: this.userId,
        };
      },
      fetchPolicy: "cache-and-network",
      update(data) {
        return data.userPrizeWeeklyEligibility;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.streak-calendar-dots {
  .sweaty-selfie {
    position: absolute;
    z-index: 2;
    left: 4px;
    top: -2px;
  }

  .nutrition-no {
    position: absolute;
    z-index: 2;
    margin-left: 9px;
    margin-top: 3px;
  }

  .sweaty-selfie-cal {
    position: absolute;
    z-index: 2;
    left: 10px;
    top: -2px;
  }

  .nutrition-no-cal {
    position: absolute;
    z-index: 2;
    left: 16px;
    top: 3px;
  }

  .v-avatar {
    margin: 6px 0;
  }

  .tracking-icon {
    margin: 0 -1px;
  }

  .mw-40 {
    width: 30px;
  }
}
.calendar-dots .v-tabs.theme--light.v-tabs--centered > .v-item-group.theme--light.v-tabs-bar {
  background-color: black;
  display: none;
  .v-slide-group__prev {
    background-color: black;
    display: none;
    visibility: none;
  }
}

.calendar-dots {
  .mw-30 {
    max-width: 30px;
  }
  .mw-40 {
    max-width: 40px;
  }

  button.v-icon.navigate-week {
    position: absolute;
    top: 25px;
    z-index: 2;
  }

  button.v-icon.navigate-week.left {
    left: 12px;
  }

  button.v-icon.navigate-week.right {
    right: 20px;
  }

  .day-tab {
    min-width: 30px;
    padding: 0px 8px;
    align-self: flex-start;
  }

  .today-border {
    border: 2px solid;
  }
}

.w-40 {
  width: 41px;
}
</style>
